import * as React from 'react'
import { createStorefrontApiClient } from '@shopify/storefront-api-client'
import commerce from '../services/commerce'

const COMMERCE_PROVIDER = process.env.GATSBY_COMMERCE_PROVIDER || 'stripe'

// In shopify mode, keep the raw client available on context for legacy consumers
const client =
  COMMERCE_PROVIDER === 'shopify'
    ? createStorefrontApiClient({
        storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
        publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
        apiVersion: '2024-04',
      })
    : null

const defaultValues = {
  // 购物车数据
  cart: null,

  // UI状态
  loading: false,
  showCartDrawer: false,

  // 操作状态
  operationLoading: {
    cart: false,
    checkout: false,
  },

  // 错误状态
  error: {
    message: null,
    type: null,
  },

  // 核心接口方法
  addToCart: () => {},
  buyNow: () => {},

  // 购物车管理方法
  updateQuantity: () => {},
  removeFromCart: () => {},
  clearCart: () => {},

  // UI控制方法
  toggleCartDrawer: () => {},

  // 数据获取方法
  refreshCart: () => {},
  getCartSummary: () => ({ totalQuantity: 0, totalAmount: '0' }),

  // 错误处理方法
  clearError: () => {},

  // 辅助方法
  isInCart: () => false,
  getItemQuantity: () => 0,

  // 兼容性方法（保持向后兼容）
  addVariantToCart: () => {},
  addMultipleVariantsToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  client,
}

export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== 'undefined'
const localStorageKey = 'shopify_cart_id'
const cartDataKey = 'shopify_cart_data'
const cartTimestampKey = 'shopify_cart_timestamp'
const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes in milliseconds

export const StoreProvider = ({ children }) => {
  // 状态管理
  const [cart, setCart] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [showCartDrawer, setShowCartDrawer] = React.useState(false)
  const [operationLoading, setOperationLoading] = React.useState({
    cart: false,
    checkout: false,
  })
  const [error, setError] = React.useState({
    message: null,
    type: null,
  })

  // 缓存管理
  const setCartItem = (cart) => {
    if (isBrowser) {
      localStorage.setItem(localStorageKey, cart.id)
      localStorage.setItem(cartDataKey, JSON.stringify(cart))
      localStorage.setItem(cartTimestampKey, Date.now().toString())
    }
    setCart(cart)
  }

  const getCachedCart = () => {
    if (!isBrowser) return null

    try {
      const cachedData = localStorage.getItem(cartDataKey)
      const timestamp = localStorage.getItem(cartTimestampKey)

      if (!cachedData || !timestamp) return null

      const cacheAge = Date.now() - parseInt(timestamp)
      if (cacheAge > CACHE_DURATION) {
        localStorage.removeItem(cartDataKey)
        localStorage.removeItem(cartTimestampKey)
        return null
      }

      return JSON.parse(cachedData)
    } catch (error) {
      console.error('Error reading cached cart data:', error)
      return null
    }
  }

  const clearCartCache = () => {
    if (isBrowser) {
      localStorage.removeItem(localStorageKey)
      localStorage.removeItem(cartDataKey)
      localStorage.removeItem(cartTimestampKey)
    }
  }

  // 错误处理
  const handleError = (error, type) => {
    console.error(`Cart ${type} error:`, error)
    setError({
      message: error.message || `购物车${type}操作失败`,
      type,
    })
  }

  const clearError = () => {
    setError({ message: null, type: null })
  }

  // 购物车初始化
  React.useEffect(() => {
    const initializeCart = async () => {
      // 首先尝试从缓存加载
      const cachedCart = getCachedCart()
      if (cachedCart) {
        setCart(cachedCart)
        return
      }

      const existingCartID = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null

      if (existingCartID && COMMERCE_PROVIDER === 'shopify') {
        try {
          const fetchedCart = await commerce.getCart(existingCartID)
          if (fetchedCart) {
            setCartItem(fetchedCart)
          } else {
            clearCartCache()
            await createNewCart()
          }
        } catch (error) {
          console.error('Error fetching existing cart:', error)
          clearCartCache()
          await createNewCart()
        }
      } else {
        await createNewCart()
      }
    }
    initializeCart()
  }, [])

  const createNewCart = async () => {
    try {
      const newCart = await commerce.createCart()
      setCartItem(newCart)
      return newCart
    } catch (error) {
      console.error('Create cart error:', error)
      handleError(error, 'create')
      throw error
    }
  }

  // === 核心接口方法 ===

  /**
   * 添加商品到购物车
   * @param {Array<{variantId: string, quantity: number}>} items - 商品列表
   */
  const addToCart = async (items) => {
    if (!items || items.length === 0) return

    setOperationLoading((prev) => ({ ...prev, cart: true }))
    clearError()

    try {
      let currentCart = cart
      if (!currentCart) {
        currentCart = await createNewCart()
        if (!currentCart) throw new Error('无法创建购物车')
      }

      const lines = items.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity || 1,
      }))

      const updatedCart = await commerce.addLines(currentCart, lines)
      if (updatedCart) {
        setCartItem(updatedCart)
        setShowCartDrawer(true)
      }
    } catch (error) {
      console.error('AddToCart error:', error)
      handleError(error, 'add')
    } finally {
      setOperationLoading((prev) => ({ ...prev, cart: false }))
    }
  }

  /**
   * 购买商品（统一结账流程）
   * @param {Array|null} items - 直接购买的商品列表，传 null 则使用当前购物车结账
   * @param {string} [customerEmail] - 用户邮箱，传给支付平台以关联订单
   */
  const buyNow = async (items, customerEmail) => {
    setOperationLoading((prev) => ({ ...prev, checkout: true }))
    clearError()

    try {
      let currentCart = cart
      if (items && items.length > 0) {
        await addToCart(items)
        currentCart = cart
      }
      if (!currentCart) {
        currentCart = await createNewCart()
      }
      await commerce.checkout(currentCart, customerEmail)
    } catch (error) {
      console.error('Buy now error:', error)
      handleError(error, 'checkout')
    } finally {
      setOperationLoading((prev) => ({ ...prev, checkout: false }))
    }
  }

  // === 购物车管理方法 ===

  const updateQuantity = async (lineId, quantity) => {
    if (!cart || !lineId) return

    setOperationLoading((prev) => ({ ...prev, cart: true }))
    clearError()

    try {
      const updatedCart = await commerce.updateLine(cart, lineId, quantity)
      if (updatedCart) setCartItem(updatedCart)
    } catch (error) {
      handleError(error, 'update')
    } finally {
      setOperationLoading((prev) => ({ ...prev, cart: false }))
    }
  }

  const removeFromCart = async (lineId) => {
    if (!cart || !lineId) return

    setOperationLoading((prev) => ({ ...prev, cart: true }))
    clearError()

    try {
      const updatedCart = await commerce.removeLine(cart, lineId)
      if (updatedCart) setCartItem(updatedCart)
    } catch (error) {
      handleError(error, 'remove')
    } finally {
      setOperationLoading((prev) => ({ ...prev, cart: false }))
    }
  }

  const clearCart = async () => {
    clearCartCache()
    setCart(null)
    // 创建新的空购物车
    await createNewCart()
  }

  // === UI控制方法 ===

  const toggleCartDrawer = (show) => {
    setShowCartDrawer(show !== undefined ? show : !showCartDrawer)
  }

  // === 数据获取方法 ===

  const refreshCart = async () => {
    if (!cart) return

    setLoading(true)
    try {
      if (COMMERCE_PROVIDER === 'shopify') {
        const fetchedCart = await commerce.getCart(cart.id)
        if (fetchedCart) setCartItem(fetchedCart)
      }
      // In stripe mode, cart is local — nothing to refresh from server
    } catch (error) {
      handleError(error, 'fetch')
    } finally {
      setLoading(false)
    }
  }

  const getCartSummary = () => {
    if (!cart) return { totalQuantity: 0, totalAmount: '0' }

    return {
      totalQuantity: cart.totalQuantity || 0,
      totalAmount: cart.cost?.totalAmount?.amount || '0',
    }
  }

  // === 辅助方法 ===

  const isInCart = (variantId) => {
    if (!cart || !cart.lines) return false

    return cart.lines.nodes.some((line) => line.merchandise.id === variantId)
  }

  const getItemQuantity = (variantId) => {
    if (!cart || !cart.lines) return 0

    const line = cart.lines.nodes.find(
      (line) => line.merchandise.id === variantId,
    )

    return line ? line.quantity : 0
  }

  // === 向后兼容方法 ===

  const addVariantToCart = async (variantId, quantity = 1) => {
    await addToCart([{ variantId, quantity }])
  }

  const addMultipleVariantsToCart = async (items) => {
    await addToCart(items)
  }

  const removeLineItem = async (lineId) => {
    await removeFromCart(lineId)
  }

  const updateLineItem = async (lineId, quantity) => {
    await updateQuantity(lineId, quantity)
  }

  // Context值
  const contextValue = {
    // 状态
    cart,
    loading,
    showCartDrawer,
    operationLoading,
    error,

    // 核心接口
    addToCart,
    buyNow,

    // 购物车管理
    updateQuantity,
    removeFromCart,
    clearCart,

    // UI控制
    toggleCartDrawer,
    setShowCartDrawer: toggleCartDrawer,

    // 数据获取
    refreshCart,
    getCartSummary,

    // 错误处理
    clearError,

    // 辅助方法
    isInCart,
    getItemQuantity,

    // 向后兼容
    addVariantToCart,
    addMultipleVariantsToCart,
    removeLineItem,
    updateLineItem,
    client,

    // 兼容旧属性
    isOpen: showCartDrawer,
    onOpen: () => toggleCartDrawer(true),
    onClose: () => toggleCartDrawer(false),
    checkout: cart,
  }

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  )
}
